/**
 * insuranceDeepLink.ts — where a merge-field pre-flight on the policy dossier sends the admin.
 *
 * TOTAL OVER THE VOCABULARY, WHICH IS THE WHOLE REASON IT IS A MODULE AND NOT A CLOSURE. The
 * instrument's pre-flight draws one "Resolve in …" control per unresolved field and every one of
 * them calls this; handed a key it has no answer for, an inline handler would paint an enabled
 * button that does nothing. So the answer is one of exactly two shapes and never `undefined`: an
 * area of this dossier, resolved in place, or a SETUP path, which is a real route even when the key
 * names nothing (`areaPath(id, null)` is the bare setup route, and the landing rule takes it from
 * there).
 *
 * AND THE FIELD IS THE SECOND ARGUMENT BECAUSE THE STEP IS NOT ENOUGH HERE. `MERGE_FIELD_STEPS`
 * routes the whole `insurance.` and `insurance_loan.` namespaces to the single step `insurance` —
 * which was exactly right while insurance was one area of contract setup, and is three areas of this
 * dossier. The instrument quotes the premium, the rate, the deposit, the weekly recovery, the term
 * and the cover period, and they are edited on three different canvases. The step gets the admin to
 * this dossier; the field says which canvas.
 *
 * THE THREE VOCABULARIES ARE CONSULTED IN ORDER, NEVER MERGED. This dossier's own keys answer first
 * — that is what `blockedStep` and the rail hand over — then the `insurance` step is split by field,
 * and everything else falls through to `resolveArea`, which is `SetupArea`'s closed union of six.
 * No function learns another's keys; `labelForArea` is the same arrangement for the sentence.
 *
 * PURE, AND REACT-FREE: it composes a path rather than navigating to one, so a test can assert the
 * totality without a router.
 */

import { areaPath, resolveArea } from '../steps';
import { resolveInsuranceArea, type InsuranceArea } from './insuranceSteps';

export type InsuranceDeepLink =
    /** An area of this dossier: the canvas changes, the page does not. */
    | { kind: 'insurance'; area: InsuranceArea }
    /** Anywhere else: the contract's own setup route, always a real path. */
    | { kind: 'setup'; path: string };

/**
 * Which canvas of this dossier a merge field belongs to — the single author of that answer, read by
 * the pre-flight through `resolveInsuranceDeepLink` and by the proof-read directly.
 *
 * THE NAMESPACE IS NOT THE ANSWER, AND THE PREMIUM KEYS ARE WHY. `agreement_merge_data` publishes
 * exactly five `insurance.` keys (20260902010000): `period_start` and `period_end`, which are the
 * policy's own dates, and `premium_amount`, `premium_paid_amount` and `premium_paid_on`, every one
 * of which it resolves from the LOAN — `insurance_loans.amount` at :1108-1109 and the loan's
 * `insurance_loan_deposits` at :1110-1113. All three are boxes on the Advance canvas. The premium
 * canvas edits `insurance_premiums` and `insurance_premium_discounts`, and neither table publishes a
 * merge key at all, so routing anything there is routing to a canvas that cannot fill it.
 *
 * THAT OVERRULES THE PLAN'S OWN LINE, deliberately and on the evidence: the approved design says
 * "`insurance.premium_*` → premium", which reads the namespace rather than the resolver. A "Resolve
 * in …" control that lands an admin on a canvas with no such box is the one dead control totality
 * exists to prevent, so the rule here is the backend's.
 *
 * POLICY IS THE FALLBACK, INCLUDING FOR NO FIELD AT ALL. A blocked slot hands over a step and no
 * field, and the policy canvas is where a year of cover starts — an admin who lands there can reach
 * either of the other two from the rail, which is not true in the other direction on a policy that
 * does not exist yet.
 */
export function insuranceAreaForField(field: string | null | undefined): InsuranceArea {
    if (!field) return 'policy';
    if (field.startsWith('insurance_loan.') || field.startsWith('insurance.premium_')) {
        return 'advance';
    }
    return 'policy';
}

/**
 * The destination for an AREA KEY or a STEP, with the merge field that produced it where there is
 * one.
 *
 * `blockedStep` and `stepForMergeField` both answer with a key rather than a route, and the three
 * vocabularies share that one type. Anything unrecognised is a setup link to the bare route rather
 * than a refusal: an admin sent to the top of the contract's own dossier can still find the field,
 * and an admin sent nowhere cannot.
 */
export function resolveInsuranceDeepLink(
    contractId: number,
    value: string | null | undefined,
    field?: string | null,
): InsuranceDeepLink {
    const insurance = resolveInsuranceArea(value);
    if (insurance) return { kind: 'insurance', area: insurance };

    const setupArea = resolveArea(value);
    // THE ONE STEP THAT IS THIS WHOLE DOSSIER. `insurance` as a setup area is what this page
    // replaced for a policy year, so a pre-flight naming it stays here and the field picks the
    // canvas — leaving the page for contract setup would send an admin to the very block the
    // dossier is authoritative over.
    if (setupArea === 'insurance') return { kind: 'insurance', area: insuranceAreaForField(field) };

    return { kind: 'setup', path: areaPath(contractId, setupArea) };
}
